import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { VERSION_FIELD_DESCRIPTOR } from './_versionField.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { SALES_ORDER, SalesOrderStatusValues, SALES_ORDER_STATUS_LABELS, } from '../types/sales-order.js';
/**
 * Descriptor for the sales order header (org/{orgId}/sales-order).
 *
 * No lifecycle fields (an order is transactional — see types/order.ts); the only
 * lifecycle is the business `status`. Keeps the `Auditable` block. `customerId`
 * is a FK to a Party with the CUSTOMER role. Totals are computed in salesOrderService.
 */
export const salesOrderDescriptor = {
    name: SALES_ORDER,
    label: 'Pedido',
    domain: 'sales',
    collectionPath: 'org/{orgId}/sales-order',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Sales order header with computed totals and a status state machine',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        // NoCache: entidade versionada (spec 0045) — getById em cache devolve versão velha e anula a detecção de conflito.
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true, description: 'Unique order identifier' },
        {
            name: 'code',
            example: 5531,
            label: 'Código',
            type: 'number',
            display: 'code',
            required: true,
            unique: true,
            autoNumber: true,
            description: 'Sales order code (manual integer, unique per organization; positive, capped at MAX_CODE_SMALL)',
        },
        {
            name: 'customerId',
            label: 'Cliente',
            type: 'string',
            required: true,
            description: 'Customer party (CUSTOMER role)',
        },
        {
            name: 'orderDate',
            example: '2026-10-15',
            label: 'Data do pedido',
            type: 'date',
            required: false,
            description: 'Date of the order',
        },
        {
            name: 'deliveryDate',
            example: '2026-11-05',
            label: 'Data de entrega',
            type: 'date',
            required: false,
            description: 'Promised delivery date (optional, informational)',
        },
        {
            name: 'status',
            example: 'confirmed',
            label: 'Status',
            type: 'enum',
            required: true,
            enumValues: [...SalesOrderStatusValues],
            enumLabels: SALES_ORDER_STATUS_LABELS,
            description: 'Sales order status (draft → confirmed → partially-shipped → shipped | cancelled)',
            defaultValue: 'draft',
        },
        {
            name: 'totalAmount',
            example: 9725,
            label: 'Valor total',
            type: 'number',
            display: 'money',
            required: false,
            description: 'Denormalized header total = Σ item.totalPrice (computed in the service)',
        },
        {
            name: 'itemCount',
            example: 1,
            label: 'Itens',
            type: 'number',
            display: 'quantity',
            required: false,
            description: 'Denormalized line count (computed in the service)',
        },
        ...AUDIT_FIELD_DESCRIPTORS,
        VERSION_FIELD_DESCRIPTOR,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'customerId',
            targetEntity: 'Party',
            relationship: 'many-to-one',
            displayField: 'displayName',
            description: 'Customer party (CUSTOMER role)',
        },
    ],
};
//# sourceMappingURL=sales-order.descriptor.js.map